// The lifecycle of a deliverable's "refresh stats" control.
//
// This used to be two lines copy-pasted into three components: set
// `refreshingStats = true` on click, and set it back to false in the
// `deliverable-updated` cable handler. That handler was the only exit, and the
// server never sent that event (RefreshDeliverableStatsJob guarded it on
// class-level sidekiq options, which `set(queue:)` does not touch — see
// test/workers/refresh_deliverable_stats_job_broadcast_test.rb), so the control
// spun forever while the stats quietly updated in the background.
//
// Fixing the broadcast is necessary but not sufficient. A binary spinner can
// only say "something is happening" or "nothing is happening", and a refresh
// has more truthful states than that: it can be queued behind a backlog, it can
// be deferred for hours by a provider rate limit (Facebook allows ~200 stat
// reads per hour PER TOKEN), it can complete without visibly changing a single
// number, it can fail, and it can be declined outright. Presenting an
// hours-long wait as an in-flight spinner is what made this read as broken.
//
// What we deliberately do NOT do: predict when a deferred job will run. We
// cannot know — it depends on a shared, per-token budget and a queue we don't
// control — so there is no ETA and no progress bar here. The states name the
// mechanism instead.
//
// Timers are injected so all of this is testable without a browser or a wall
// clock — see test/javascript/refreshStatsState.test.js.

export const REFRESH_STATES = {
  // At rest. The control shows the deliverable's own reporting status and when
  // it last updated — the signal that matters when nothing is happening.
  IDLE: "idle",
  // We asked, and a completion is expected shortly.
  REFRESHING: "refreshing",
  // Nothing came back in the window. The job exists but hasn't run: queued
  // behind a backlog, or deferred by a provider rate limit. Not an error.
  WAITING: "waiting",
  // The completion arrived. Held briefly, because a successful refresh often
  // changes no visible number and the user still needs to know their click did
  // something.
  DONE: "done",
  // Nothing was enqueued because the stats were fetched inside their refresh
  // window (YouTube's quota throttle). Not a problem: the numbers are current.
  // Held briefly, like DONE, then back to rest.
  UP_TO_DATE: "up_to_date",
  // The server told us nothing was or will be enqueued.
  UNAVAILABLE: "unavailable",
  // The request itself failed.
  FAILED: "failed",
};

// How long to keep spinning before falling back to "waiting". A high_priority
// refresh normally lands within seconds; this is generous enough to cover a slow
// provider and short enough that a deferred job stops reading as in-flight.
export const REFRESH_SPINNER_TIMEOUT_MS = 45000;

// How long the "just updated" confirmation stays up before the control returns
// to showing the deliverable's status.
export const REFRESH_DONE_TIMEOUT_MS = 6000;

// Statuses from GET /deliverables/:id/refresh.json. "skipped" and
// "recently_refreshed" are the ones that matter: no job was or will be
// enqueued, so no broadcast is ever coming. Only "skipped" is a problem.
const KEEPS_WAITING = ["queued", "already_queued"];

// Fomantic icons, reusing the vocabulary Deliverable#reporting_status already
// emits (circle check / circle exclamation / cloud-slash / hourglass half) so
// the control never invents a second language for the same idea.
const ICONS = {
  [REFRESH_STATES.IDLE]: "icon sync",
  [REFRESH_STATES.REFRESHING]: "icon sync loading",
  [REFRESH_STATES.WAITING]: "icon hourglass half",
  [REFRESH_STATES.DONE]: "icon circle check",
  [REFRESH_STATES.UP_TO_DATE]: "icon circle check",
  [REFRESH_STATES.UNAVAILABLE]: "icon circle exclamation",
  [REFRESH_STATES.FAILED]: "icon circle exclamation",
};

const PLATFORM_LABELS = {
  url: "Google Analytics",
  tik_tok: "TikTok",
  youtube: "YouTube",
  facebook: "Facebook",
  instagram: "Instagram",
  instagram_stories: "Instagram",
  linkedin: "LinkedIn",
  pinterest: "Pinterest",
  twitter: "X",
  beehiiv: "beehiiv",
  mailchimp: "Mailchimp",
  activecampaign: "ActiveCampaign",
  convertkit: "Kit",
  short_io: "Short.io",
  bitly: "Bitly",
};

export function platformLabel(inferredType) {
  if (!inferredType) return "This platform";
  if (PLATFORM_LABELS[inferredType]) return PLATFORM_LABELS[inferredType];
  return inferredType
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function refreshIconClass(state) {
  return ICONS[state] || ICONS[REFRESH_STATES.IDLE];
}

function ago(count, unit) {
  return `${count} ${unit}${count === 1 ? "" : "s"} ago`;
}

// "3 hours ago". Plain arithmetic rather than moment, so this stays a pure,
// dependency-free function the tests can drive with a fixed clock.
export function relativeUpdatedLabel(value, now) {
  if (!value) return null;

  const then = value instanceof Date ? value : new Date(value);
  if (isNaN(then.getTime())) return null;

  const current = now || new Date();
  // Clamped: a client clock running ahead of the server shouldn't render a
  // negative age.
  const seconds = Math.max(0, Math.floor((current - then) / 1000));
  if (seconds < 45) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return ago(minutes, "minute");

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return ago(hours, "hour");

  const days = Math.floor(hours / 24);
  if (days < 7) return ago(days, "day");

  // Calendar months from here up, not a fixed number of days: two calendar
  // years is 730 days, and 730 / 365.25 floors to "1 year ago".
  const months =
    (current.getUTCFullYear() - then.getUTCFullYear()) * 12 +
    (current.getUTCMonth() - then.getUTCMonth()) -
    (current.getUTCDate() < then.getUTCDate() ? 1 : 0);

  if (months < 1) return ago(Math.floor(days / 7), "week");
  if (months < 12) return ago(months, "month");
  return ago(Math.floor(months / 12), "year");
}

// "in 12 minutes". The mirror of relativeUpdatedLabel, for a refresh control
// that throttles itself and has to say when clicking will actually do
// something. Returns null once the moment has passed (or when there's nothing
// to say), so the caller shows no countdown rather than "in 0 minutes".
export function relativeCountdownLabel(value, now) {
  if (!value) return null;

  const then = value instanceof Date ? value : new Date(value);
  if (isNaN(then.getTime())) return null;

  const seconds = Math.floor((then - (now || new Date())) / 1000);
  if (seconds <= 0) return null;
  if (seconds < 60) return "in less than a minute";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;

  const hours = Math.round(minutes / 60);
  return `in ${hours} hour${hours === 1 ? "" : "s"}`;
}

// The one line of text the control shows. `context` carries what only the
// component knows: which platform this deliverable is on, when it last updated,
// and the deliverable's own reporting-status message (which the model already
// computes, and which explains a failure far better than anything generic).
export function refreshTitle(state, context = {}) {
  switch (state) {
    case REFRESH_STATES.REFRESHING:
      return "Refreshing stats…";
    // Short on purpose. This lives in a tooltip on a narrow card column, and an
    // explanatory paragraph there wraps to fifteen lines and reads as noise —
    // it has to say the one thing that stops the user clicking again. It names
    // the rate limit and promises nothing about when: a throttle-deferred job's
    // run time depends on a shared per-token budget we don't control, so there
    // is no ETA to give.
    case REFRESH_STATES.WAITING:
      return `Still queued — ${platformLabel(
        context.inferredType
      )} limits how often stats refresh`;
    case REFRESH_STATES.DONE:
      return "Stats updated just now";
    case REFRESH_STATES.UP_TO_DATE:
      return context.updatedLabel
        ? `Updated ${context.updatedLabel}, so there's nothing new to pull yet`
        : "Stats are current, so there's nothing new to pull yet";
    case REFRESH_STATES.UNAVAILABLE:
      return (
        context.statusMessage ||
        "Not refreshing — open this deliverable to see why"
      );
    case REFRESH_STATES.FAILED:
      return "Couldn't start a refresh — try again";
    default:
      break;
  }

  // At rest, the honest answer is "here's how current this is". Prefer the
  // model's own message when it has something to say (a reconnect prompt, an
  // error), because that is more useful than a timestamp.
  if (context.statusMessage && context.statusOk === false)
    return context.statusMessage;
  if (context.updatedLabel)
    return `Updated ${context.updatedLabel} — click to refresh`;
  return "Refresh stats";
}

export function createRefreshTracker(options = {}) {
  const timeoutMs = options.timeoutMs || REFRESH_SPINNER_TIMEOUT_MS;
  const doneMs = options.doneMs || REFRESH_DONE_TIMEOUT_MS;
  const setTimer = options.setTimer || ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = options.clearTimer || ((handle) => clearTimeout(handle));
  const onChange = options.onChange || (() => {});
  // Called when the spinner window elapses. The component re-reads the
  // deliverable from the server rather than guessing what happened — a tab that
  // was asleep can miss the broadcast entirely, and "go and check" is the only
  // honest way to tell that apart from "still queued".
  const onTimeout = options.onTimeout || (() => {});

  let state = REFRESH_STATES.IDLE;
  let handle = null;

  function disarm() {
    if (handle !== null) {
      clearTimer(handle);
      handle = null;
    }
  }

  function transition(next) {
    if (state === next) return;
    state = next;
    onChange(state);
  }

  function settle(next) {
    disarm();
    transition(next);
  }

  return {
    get state() {
      return state;
    },

    get spinning() {
      return state === REFRESH_STATES.REFRESHING;
    },

    // The user clicked. Re-arms rather than stacking timers, so a double click
    // can't leave a stray one behind to settle a later refresh.
    start() {
      disarm();
      transition(REFRESH_STATES.REFRESHING);
      handle = setTimer(() => {
        handle = null;
        transition(REFRESH_STATES.WAITING);
        onTimeout();
      }, timeoutMs);
    },

    // What the refresh request answered. Anything unrecognised is treated as
    // "a completion is still coming" — the fallback covers being wrong.
    enqueued(status) {
      if (state !== REFRESH_STATES.REFRESHING) return;
      if (status === "skipped") settle(REFRESH_STATES.UNAVAILABLE);
      if (status === "recently_refreshed") {
        settle(REFRESH_STATES.UP_TO_DATE);
        handle = setTimer(() => {
          handle = null;
          transition(REFRESH_STATES.IDLE);
        }, doneMs);
      }
      if (KEEPS_WAITING.indexOf(status) !== -1) return;
    },

    failed() {
      if (state !== REFRESH_STATES.REFRESHING) return;
      settle(REFRESH_STATES.FAILED);
    },

    // A `deliverable-updated` broadcast arrived, or a re-check found the stats
    // had moved. That event is shared — uploads, Textract and the provider
    // webhooks all send it — so one arriving while we are at rest is not ours
    // to react to.
    completed() {
      if (state === REFRESH_STATES.IDLE) return;
      disarm();
      transition(REFRESH_STATES.DONE);
      handle = setTimer(() => {
        handle = null;
        transition(REFRESH_STATES.IDLE);
      }, doneMs);
    },

    destroy() {
      disarm();
    },
  };
}
